import React, { useEffect, useRef, useState } from 'react';
import { Box, Grid2 } from '@mui/material';
import * as Yup from 'yup';
import CustomIconButton from '@ui-components/IconButton/CustomIconButton';
import BackIcon from '@assets/images/BackIcon.svg';
import CustomTypography from '@ui-components/Typography/CustomTypography';
import { useParams, useNavigate, useLocation } from 'react-router';
import {
  FACILITY_ROUTE_PATH,
  FACILITY_VIEW_ROUTE_PATH,
} from '../../routers/routePaths';
import CustomTextField from '@ui-components/TextField/CustomTextField';
import CustomSelect from '@ui-components/Select/CustomSelect';
import CustomAutoComplete from '@ui-components/AutoComplete/CustomAutoComplete';
import CustomDatePicker from '@ui-components/DateTimePicker/CustomDatePicker';
import CustomSwitch from '@ui-components/Switch/CustomSwitch';
import CustomDivider from '@ui-components/Divider/CustomDivider';
import { CustomAutoCompleteSingleSelection } from '@qler-view/shared_lib/src';
import CustomButton from '@ui-components/Button/CustomButton';
import {
  useCheckShotNameUniq,
  useGetFacilityDetailsById,
  useGetFacilityMetaData,
  useSaveOrUpdateFacility,
} from '../../API/hooks/FacilityHook';
import CustomAutoCompleteMultiDetailed from '@ui-components/AutoComplete/CustomAutoCompleteMultiDetailed';
import { Form, Formik } from 'formik';
import type { FormikProps } from 'formik';
import type { FacilityData } from '../../API/services/FacilityService';
import moment from 'moment';
import formatPhoneNumber from '../components/FormatPhoneNumber';
import validatePostalCode from '../components/ValidatePostalCode';
import type { Option } from '@ui-components/AutoComplete/CustomAutoComplete';
import ConfirmDiscardDialog from '../components/ConfirmDiscardDialog';
import { useDebounce } from 'use-debounce';
import facilityInitialValues from './facilitiesInitialValues';

type PartnerDetails = {
  name: string;
  type: string;
  businessId: string;
  status: string;
};

const AddNewFacility: React.FC = () => {
  const formikRef = useRef<FormikProps<FacilityData>>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const userId = localStorage.getItem('userId') ?? '0';
  const { facilityId } = useParams<{ facilityId?: string }>();
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [facilityShotName, setFacilityShotName] = useState<string>('');
  const [facilitySubmitAction, setFacilitySubmitAction] = useState<string>('');

  const { facilityMetaData } = useGetFacilityMetaData(userId);

  const [initialValues, setInitialValues] = useState(facilityInitialValues);

  const { FacilityDetailsById, isLoading } = useGetFacilityDetailsById(
    userId,
    Number(facilityId)
  );

  const [debouncedShotName] = useDebounce(facilityShotName, 500);
  const { ShotNameUniq } = useCheckShotNameUniq(
    debouncedShotName ?? '',
    facilityId ? Number(facilityId) : undefined
  );
  const shortNameTakenRef = useRef<{ value: string; message?: string } | null>(
    null
  );

  useEffect(() => {
    if (FacilityDetailsById) {
      setInitialValues({
        facilityInfo: FacilityDetailsById.facilityInfo,
        contactDetails: FacilityDetailsById.contactDetails,
        itSystemDetails: FacilityDetailsById.itSystemDetails,
        address: FacilityDetailsById.address,
        description: FacilityDetailsById.description,
      });
    }
  }, [FacilityDetailsById]);

  useEffect(() => {
    if (ShotNameUniq && formikRef.current) {
      if (!ShotNameUniq.isAvailable) {
        shortNameTakenRef.current = {
          value: debouncedShotName,
          message: ShotNameUniq.message,
        };
        formikRef.current.setFieldTouched(
          'facilityInfo.shortName',
          true,
          false
        );
        formikRef.current.setFieldError(
          'facilityInfo.shortName',
          ShotNameUniq.message
        );
      } else {
        shortNameTakenRef.current = null;
      }
    }
  }, [ShotNameUniq]);

  const onSuccess = () => {
    if (facilityId) {
      navigate(`${FACILITY_ROUTE_PATH}?toast=update`);
    } else {
      navigate(`${FACILITY_ROUTE_PATH}?toast=success`);
    }
  };

  const { mutate: saveOrUpdateFacility } = useSaveOrUpdateFacility(onSuccess);

  const handleFacilitySaveOrUpdate = async (
    facilityData: typeof facilityInitialValues
  ) => {
    const { ...payload } = facilityData;

    if (facilitySubmitAction) {
      saveOrUpdateFacility({
        userId,
        action: facilitySubmitAction,
        ...payload,
      });
    }
  };

  const handleOpenConfirmDialog = (state: boolean) => {
    setOpenConfirmDialog(state);
  };

  const customLinkTypeId = facilityMetaData?.videoConferenceTypeId?.find(
    (item) => item.name.toLowerCase() === 'custom link'
  )?.id;

  const facilityValidationSchema = Yup.object({
    // REQUIRED fields
    facilityInfo: Yup.object().shape({
      name: Yup.string().required('Facility name is required'),
      assignedTo: Yup.number()
        // .typeError('Assigned To must be a number')
        .required('Assigned To is required')
        .min(1, 'Assigned To is required'),

      partnerId: Yup.number()
        // .typeError('Partner ID must be a number')
        .required('Partner ID is required')
        .min(1, 'Partner ID is required'),

      shortName: Yup.string()
        .test('len', 'Short Name must be between 3 and 8 characters', (val) =>
          val ? val.length >= 3 && val.length <= 8 : true
        )
        .test('unique', (val, context) => {
          const taken = shortNameTakenRef.current;
          return taken && val === taken.value
            ? context.createError({ message: taken.message })
            : true;
        })
        .required('Short Name is required'),
      intakeFormId: Yup.number()
        // .typeError('Assigned To must be a number')
        .required('Intake Form is required')
        .min(1, 'Intake Form is required'),
      videoConferenceTypeId: Yup.number()
        .required('Video Conferencing is required')
        .min(1, 'Video Conferencing is required'),
      customLink: Yup.string().when('videoConferenceTypeId', {
        is: (value: number) => value === customLinkTypeId,
        then: (schema) =>
          schema
            .required('Custom Link is required')
            .url('Custom Link must be a valid URL'),

        otherwise: (schema) => schema.notRequired(),
      }),
    }),
    // OPTIONAL but validate if value exists
    contactDetails: Yup.object().shape({
      primaryPhone: Yup.string()
        .nullable()
        .test(
          'valid-primaryPhone',
          'Phone must be in format (123) 456-7890',
          (value) => !value || /^\(\d{3}\)\s\d{3}-\d{4}$/.test(value)
        ),

      edPhone: Yup.string()
        .nullable()
        .test(
          'valid-edPhone',
          'Phone must be in format (123) 456-7890',
          (value) => !value || /^\(\d{3}\)\s\d{3}-\d{4}$/.test(value)
        ),

      icuPhone: Yup.string()
        .nullable()
        .test(
          'valid-icuPhone',
          'Phone must be in format (123) 456-7890',
          (value) => !value || /^\(\d{3}\)\s\d{3}-\d{4}$/.test(value)
        ),
      megSurgPhone: Yup.string()
        .nullable()
        .test(
          'valid-megSurgPhone',
          'Phone must be in format (123) 456-7890',
          (value) => !value || /^\(\d{3}\)\s\d{3}-\d{4}$/.test(value)
        ),
      itHelpDeskEmail: Yup.string().nullable().email('Invalid email'),
      itHelpDeskPhone: Yup.string()
        .nullable()
        .test(
          'valid-itHealpDeskPhone',
          'Phone must be in format (123) 456-7890',
          (value) => !value || /^\(\d{3}\)\s\d{3}-\d{4}$/.test(value)
        ),

      otherFax: Yup.string()
        .nullable()
        .test(
          'valid-fax',
          'Phone must be in format (123) 456-7890',
          (value) => !value || /^\(\d{3}\)\s\d{3}-\d{4}$/.test(value)
        ),

      otherFaxDescription: Yup.string().max(
        500,
        'Description must be no more than 500 characters long.'
      ),

      other: Yup.string().max(
        500,
        'Description must be no more than 500 characters long.'
      ),

      secureFax: Yup.string()
        .nullable()
        .test(
          'valid-secure-Fax',
          'Phone must be in format (123) 456-7890',
          (value) => !value || /^\(\d{3}\)\s\d{3}-\d{4}$/.test(value)
        ),
    }),

    itSystemDetails: Yup.object().shape({
      itNotes: Yup.string().max(
        500,
        'Description must be no more than 500 characters long.'
      ),
    }),

    address: Yup.object().shape({
      postalCode: Yup.string()
        .transform((value) => (value === '0' || value === 0 ? null : value))
        .nullable()
        .test(
          'valid-postalCode',
          'Postal Code must be exactly 5 digits',
          (value) => !value || /^\d{5}$/.test(value)
        ),
    }),

    description: Yup.string().max(
      1000,
      'Description must be no more than 1000 characters long.'
    ),
  });

  const mapUsStatesToOptions = (states: string[] | undefined): Option[] => {
    return (
      states?.map((state) => ({
        name: state,
        id: state,
      })) ?? []
    );
  };

  const handleBackButtonClick = () => {
    if (facilityId && !location.state?.isFacilityList) {
      navigate(FACILITY_VIEW_ROUTE_PATH + `/${facilityId}`, {
        replace: true,
      });
    } else {
      navigate(FACILITY_ROUTE_PATH);
    }
  };

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="200px"
      >
        <CustomTypography
          id="loading-text"
          variant="h5"
          className="text-align-center pt-32"
          color="textDisabled"
        >
          Loading...
        </CustomTypography>
      </Box>
    );
  }

  return (
    <Box>
      <Box className="pt-20 pb-20">
        <Grid2 container>
          <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
            <Box className="text-align-left display-flex">
              <CustomIconButton
                id="back-button"
                icon={<BackIcon />}
                onClick={() => {
                  handleBackButtonClick();
                }}
              ></CustomIconButton>

              <CustomTypography
                className="pl-8"
                variant="h4"
                id="add-new-facility-text"
              >
                {facilityId ? 'Edit Facility' : 'Add New Facility'}
              </CustomTypography>
              <CustomTypography
                className="pl-12"
                variant="h4"
                id="partner-business-id"
              >
                {facilityId
                  ? `(${initialValues.facilityInfo.facilityBusinessId})`
                  : ''}
              </CustomTypography>
            </Box>
          </Grid2>
        </Grid2>
      </Box>
      <Formik
        innerRef={formikRef}
        initialValues={initialValues}
        validateOnMount={false}
        validationSchema={facilityValidationSchema}
        onSubmit={(values) => {
          console.log('Form submitted:', values);
          handleFacilitySaveOrUpdate(values);
        }}
        enableReinitialize
      >
        {({
          values,
          errors,
          touched,
          // isValid,
          // dirty,
          handleSubmit,
          handleBlur,
          setFieldValue,
          setFieldTouched,
        }) => {
          console.log('err', errors);
          return (
            <Form>
              <Box className="form-background p-24">
                <Grid2 container>
                  <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                    <CustomTypography
                      variant="h5"
                      className="text-align-left pb-32"
                      id="facility-info-text"
                    >
                      Facility Information
                    </CustomTypography>
                  </Grid2>
                </Grid2>
                <Box className="display-flex">
                  <Grid2 container spacing={4} className="width-80">
                    <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                      <CustomTypography
                        id="facility-name-label"
                        variant="body3"
                        color="textPrimary"
                        className="text-align-left pb-12"
                      >
                        Facility Name <span className="color-red">*</span>
                      </CustomTypography>
                      <CustomTextField
                        id="txt-facility-name"
                        name="facilityName"
                        placeholder="Type Facility Name"
                        size="small"
                        value={values.facilityInfo.name}
                        onChange={(event) => {
                          setFieldValue(
                            'facilityInfo.name',
                            event.target.value
                          );
                        }}
                      ></CustomTextField>
                      {errors?.facilityInfo?.name &&
                        touched?.facilityInfo?.name && (
                          <CustomTypography
                            id="validate-facility-name"
                            color="error"
                            variant="body3"
                            className="pt-8 text-align-left"
                          >
                            {errors.facilityInfo.name}
                          </CustomTypography>
                        )}
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                      <CustomTypography
                        id="facility-alias-label"
                        variant="body3"
                        color="textPrimary"
                        className="text-align-left pb-12"
                      >
                        Alias
                      </CustomTypography>
                      <CustomTextField
                        id="txt-facility-alias"
                        placeholder="Type alias Name"
                        size="small"
                        // inputProps={{ maxLength: 50 }}
                        value={values.facilityInfo.alias}
                        onChange={(event) => {
                          setFieldValue(
                            'facilityInfo.alias',
                            event.target.value
                          );
                        }}
                      ></CustomTextField>
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                      <CustomTypography
                        id="facility-status-label"
                        variant="body3"
                        color="textPrimary"
                        className="text-align-left pb-12"
                      >
                        Status
                      </CustomTypography>
                      <Box className="text-align-left">
                        <CustomSwitch
                          id="facility-status-switch"
                          checked={values.facilityInfo.status}
                          onChange={(event) => {
                            setFieldValue(
                              'facilityInfo.status',
                              event.target.checked
                            );
                          }}
                          labelPlacement="end"
                          label={
                            values.facilityInfo.status ? 'Active' : 'Inactive'
                          }
                        ></CustomSwitch>
                      </Box>
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                      <CustomTypography
                        id="partner-label"
                        variant="body3"
                        color="textPrimary"
                        className="text-align-left pb-12"
                      >
                        Partner <span className="color-red">*</span>
                      </CustomTypography>
                      <CustomAutoCompleteMultiDetailed<PartnerDetails>
                        options={facilityMetaData?.partners ?? []}
                        value={
                          facilityMetaData?.partners
                            ? facilityMetaData.partners.find(
                                (partner) =>
                                  partner.id === values.facilityInfo.partnerId
                              ) || null
                            : null
                        }
                        onChange={(newValue) =>
                          setFieldValue(
                            'facilityInfo.partnerId',
                            newValue?.id ?? ''
                          )
                        }
                        mainLabel="name" // main text shown
                        detailFields={[
                          { field: 'type', prefix: 'Type: ' },
                          { field: 'businessId', prefix: 'ID: ' },
                          { field: 'status', prefix: 'Status: ' },
                        ]}
                        placeholder="Search Partner"
                        noOptionsText="No Partners Found"
                      />
                      {errors?.facilityInfo?.partnerId &&
                        touched?.facilityInfo?.partnerId && (
                          <CustomTypography
                            id="validate-facility-partner"
                            color="error"
                            variant="body3"
                            className="pt-8 text-align-left"
                          >
                            {errors.facilityInfo.partnerId}
                          </CustomTypography>
                        )}
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                      <CustomTypography
                        id="facility-short-name-label"
                        variant="body3"
                        color="textPrimary"
                        className="text-align-left pb-12"
                      >
                        Facility Short Name <span className="color-red">*</span>
                      </CustomTypography>
                      <CustomTextField
                        id="txt-facility-shot-name"
                        placeholder="Type Facility Short Name"
                        size="small"
                        value={values.facilityInfo.shortName}
                        onChange={(event) => {
                          setFieldValue(
                            'facilityInfo.shortName',
                            event.target.value
                          );
                          setFacilityShotName(event.target.value || '');
                        }}
                        onBlur={() =>
                          setFieldTouched('facilityInfo.shortName', true)
                        }
                      />
                      {errors?.facilityInfo?.shortName &&
                        touched?.facilityInfo?.shortName && (
                          <CustomTypography
                            id="validate-facility-shortName"
                            color="error"
                            variant="body3"
                            className="pt-8 text-align-left"
                          >
                            {errors.facilityInfo.shortName}
                          </CustomTypography>
                        )}
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                      <CustomTypography
                        id="facility-services-provided-label"
                        variant="body3"
                        color="textPrimary"
                        className="text-align-left pb-12"
                      >
                        Services Provided
                      </CustomTypography>
                      <CustomAutoComplete
                        id="facility-services-provided"
                        placeholder="Select Services Provided"
                        size="small"
                        options={facilityMetaData?.servicesProvided ?? []}
                        value={values.facilityInfo.servicesProvided}
                        onChange={(selectedOptions) => {
                          setFieldValue(
                            'facilityInfo.servicesProvided',
                            selectedOptions
                          );
                        }}
                        disableCloseOnSelect={true}
                        limitTags={2}
                        noOptionsText="No Servidces Provided Found"
                      />
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                      <CustomTypography
                        id="facility-type-label"
                        variant="body3"
                        color="textPrimary"
                        className="text-align-left pb-12"
                      >
                        Facility Type
                      </CustomTypography>
                      <CustomSelect
                        id="facility-type"
                        placeholder="Select Facility Type"
                        size="small"
                        options={facilityMetaData?.facilityTypes ?? []}
                        onChange={(value) => {
                          setFieldValue(
                            'facilityInfo.facilityTypeId',
                            Number(value)
                          );
                        }}
                        selectedValue={values.facilityInfo.facilityTypeId}
                        fullWidth
                      />
                    </Grid2>

                    <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                      <CustomTypography
                        id="facility-website-label"
                        variant="body3"
                        color="textPrimary"
                        className="text-align-left pb-12"
                      >
                        Website
                      </CustomTypography>
                      <CustomTextField
                        id="txt-facility-website"
                        placeholder="Type Website Name"
                        size="small"
                        value={values.facilityInfo.website}
                        onChange={(event) => {
                          setFieldValue(
                            'facilityInfo.website',
                            event.target.value
                          );
                        }}
                      ></CustomTextField>
                    </Grid2>

                    <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                      <CustomTypography
                        id="video-conferencing-label"
                        variant="body3"
                        color="textPrimary"
                        className="text-align-left pb-12"
                      >
                        Video Conferencing <span className="color-red">*</span>
                      </CustomTypography>
                      <CustomSelect
                        id="video-conferencing-select"
                        placeholder="Select Video Conferencing"
                        size="small"
                        onChange={(value) => {
                          setFieldValue(
                            'facilityInfo.videoConferenceTypeId',
                            Number(value)
                          );
                        }}
                        selectedValue={
                          values.facilityInfo.videoConferenceTypeId
                        }
                        options={facilityMetaData?.videoConferenceTypeId ?? []}
                        fullWidth
                      />
                      {errors?.facilityInfo?.videoConferenceTypeId &&
                        touched?.facilityInfo?.videoConferenceTypeId && (
                          <CustomTypography
                            id="validate-video-conferencing"
                            color="error"
                            variant="body3"
                            className="pt-8 text-align-left"
                          >
                            {errors.facilityInfo.videoConferenceTypeId}
                          </CustomTypography>
                        )}
                    </Grid2>
                    {values.facilityInfo.videoConferenceTypeId ===
                      customLinkTypeId && (
                      <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                        <CustomTypography
                          id="custom-link-label"
                          variant="body3"
                          color="textPrimary"
                          className="text-align-left pb-12"
                        >
                          Custom Link <span className="color-red">*</span>
                        </CustomTypography>
                        <CustomTextField
                          id="txt-custom-link"
                          placeholder="Type Custom Link"
                          size="small"
                          type="text"
                          value={values.facilityInfo.customLink}
                          onChange={(event) => {
                            setFieldValue(
                              'facilityInfo.customLink',
                              event.target.value
                            );
                          }}
                        />
                        {errors?.facilityInfo?.customLink &&
                          touched?.facilityInfo?.customLink && (
                            <CustomTypography
                              id="validate-video-conferencing"
                              color="error"
                              variant="body3"
                              className="pt-8 text-align-left"
                            >
                              {errors.facilityInfo.customLink}
                            </CustomTypography>
                          )}
                      </Grid2>
                    )}

                    <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                      <CustomTypography
                        id="intake-form-label"
                        variant="body3"
                        color="textPrimary"
                        className="text-align-left pb-12"
                      >
                        Intake Form <span className="color-red">*</span>
                      </CustomTypography>
                      <CustomAutoCompleteSingleSelection
                        id="intake-form"
                        placeholder="Select Intake Form"
                        size="small"
                        options={facilityMetaData?.intakeForms ?? []}
                        value={
                          facilityMetaData?.intakeForms?.find(
                            (x) => x.id === values.facilityInfo.intakeFormId
                          ) ?? null
                        }
                        onChange={(selectedOptions) => {
                          const selectedId = selectedOptions
                            ? selectedOptions.id
                            : null;
                          setFieldValue(
                            'facilityInfo.intakeFormId',
                            selectedId
                          );
                        }}
                        noOptionsText="No Intake Form Found"
                      />
                      {errors?.facilityInfo?.intakeFormId &&
                        touched?.facilityInfo?.intakeFormId && (
                          <CustomTypography
                            id="validate-intake-form-error"
                            color="error"
                            variant="body3"
                            className="pt-8 text-align-left"
                          >
                            {errors.facilityInfo.intakeFormId}
                          </CustomTypography>
                        )}
                    </Grid2>

                    <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                      <CustomTypography
                        id="minimum-age-label"
                        variant="body3"
                        color="textPrimary"
                        className="text-align-left pb-12"
                      >
                        Minimum Age Required
                      </CustomTypography>
                      <CustomTextField
                        id="txt-minimum-age-required"
                        placeholder="Type Minimum Age"
                        size="small"
                        type="number"
                        value={
                          Number(values.facilityInfo.minimumAgeRequired) === 0
                            ? ''
                            : values.facilityInfo.minimumAgeRequired
                        }
                        onChange={(event) => {
                          setFieldValue(
                            'facilityInfo.minimumAgeRequired',
                            event.target.value
                          );
                        }}
                      ></CustomTextField>
                    </Grid2>

                    <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                      <CustomTypography
                        id="active-facility-since-label"
                        variant="body3"
                        color="textPrimary"
                        className="text-align-left pb-12"
                      >
                        Active Facility Since
                      </CustomTypography>
                      <CustomDatePicker
                        id="active-since-date-picker"
                        format="MM-DD-YYYY"
                        value={moment(values.facilityInfo.activeFacilitySince)}
                        onChange={(date) => {
                          setFieldValue(
                            'facilityInfo.activeFacilitySince',
                            date ? date.format('MM-DD-YYYY') : ''
                          );
                        }}
                      ></CustomDatePicker>
                    </Grid2>

                    <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                      <CustomTypography
                        id="facility-termination-label"
                        variant="body3"
                        color="textPrimary"
                        className="text-align-left pb-12"
                      >
                        Termination Date
                      </CustomTypography>
                      <CustomDatePicker
                        id="facility-termination-date-picker"
                        format="MM-DD-YYYY"
                        value={moment(values.facilityInfo.terminationDate)}
                        onChange={(date) => {
                          setFieldValue(
                            'facilityInfo.terminationDate',
                            date ? date.format('MM-DD-YYYY') : ''
                          );
                        }}
                      ></CustomDatePicker>
                    </Grid2>

                    <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                      <CustomTypography
                        id="facility-assigned-to-label"
                        variant="body3"
                        color="textPrimary"
                        className="text-align-left pb-12"
                      >
                        Assigned To <span className="color-red">*</span>
                      </CustomTypography>
                      <CustomSelect
                        id="facility-assigned-to-select"
                        placeholder="Select Assigned To"
                        size="small"
                        selectedValue={values.facilityInfo.assignedTo}
                        options={facilityMetaData?.assignedTo ?? []}
                        onChange={(value) => {
                          setFieldValue(
                            'facilityInfo.assignedTo',
                            Number(value)
                          );
                        }}
                        fullWidth
                        displayEmpty
                        emptyOptionLabel="No Assignees"
                      />
                      {errors?.facilityInfo?.assignedTo &&
                        touched?.facilityInfo?.assignedTo && (
                          <CustomTypography
                            id="validate-diagnosis"
                            color="error"
                            variant="body3"
                            className="pt-8 text-align-left"
                          >
                            {errors.facilityInfo.assignedTo}
                          </CustomTypography>
                        )}
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                      <CustomTypography
                        id="facility-Hide-PHI-label"
                        variant="body3"
                        color="textPrimary"
                        className="text-align-left pb-12"
                      >
                        Hide PHI
                      </CustomTypography>
                      <Box className="text-align-left">
                        <CustomSwitch
                          id="facility-Hide-PHI-switch"
                          checked={values.facilityInfo.isHidePhiAfterCompletion}
                          onChange={(event) => {
                            setFieldValue(
                              'facilityInfo.isHidePhiAfterCompletion',
                              event.target.checked
                            );
                          }}
                          labelPlacement="end"
                          label={
                            values.facilityInfo.isHidePhiAfterCompletion
                              ? 'On'
                              : 'Off'
                          }
                        ></CustomSwitch>
                      </Box>
                    </Grid2>
                  </Grid2>
                </Box>
                <Box className="pt-32">
                  <CustomDivider />
                </Box>
                <Box className="pt-32">
                  <Grid2 container spacing={4} className="width-80">
                    <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                      <CustomTypography
                        variant="h5"
                        className="text-align-left pb-32"
                        id="facility-key-contacts-text"
                      >
                        Key Phone Numbers and Email
                      </CustomTypography>
                    </Grid2>
                  </Grid2>
                  <Grid2 container spacing={4} className="width-80">
                    <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                      <CustomTypography
                        id="facility-primary-phone-label"
                        variant="body3"
                        color="textPrimary"
                        className="text-align-left pb-12"
                      >
                        Primary Phone
                      </CustomTypography>
                      <CustomTextField
                        id="txt-facility-primary-phone"
                        placeholder="Type Primary Phone"
                        size="small"
                        value={values.contactDetails.primaryPhone}
                        onChange={(event) => {
                          const formatted = formatPhoneNumber(
                            event.target.value
                          );
                          setFieldValue(
                            'contactDetails.primaryPhone',
                            formatted
                          );
                        }}
                        onBlur={(event) => {
                          const formatted = formatPhoneNumber(
                            event.target.value
                          );
                          setFieldValue(
                            'contactDetails.primaryPhone',
                            formatted
                          );
                        }}
                      />
                      {errors?.contactDetails?.primaryPhone && (
                        <CustomTypography
                          id="validate-primary-phone"
                          color="error"
                          variant="body3"
                          className="pt-8 text-align-left"
                        >
                          {errors.contactDetails.primaryPhone}
                        </CustomTypography>
                      )}
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                      <CustomTypography
                        id="facility-IT-help-desk-phone-label"
                        variant="body3"
                        color="textPrimary"
                        className="text-align-left pb-12"
                      >
                        IT Help Desk Phone
                      </CustomTypography>
                      <CustomTextField
                        id="txt-facility-IT-help-desk-phone"
                        placeholder="Type IT Help Desk Phone"
                        size="small"
                        value={values.contactDetails.itHelpDeskPhone}
                        onChange={(event) => {
                          const formatted = formatPhoneNumber(
                            event.target.value
                          );
                          setFieldValue(
                            'contactDetails.itHelpDeskPhone',
                            formatted
                          );
                        }}
                        onBlur={(event) => {
                          const formatted = formatPhoneNumber(
                            event.target.value
                          );
                          setFieldValue(
                            'contactDetails.itHelpDeskPhone',
                            formatted
                          );
                        }}
                      ></CustomTextField>
                      {errors?.contactDetails?.itHelpDeskPhone && (
                        <CustomTypography
                          id="validate-it-help-desk-phone"
                          color="error"
                          variant="body3"
                          className="pt-8 text-align-left"
                        >
                          {errors.contactDetails.itHelpDeskPhone}
                        </CustomTypography>
                      )}
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                      <CustomTypography
                        id="facility-IT-help-desk-label"
                        variant="body3"
                        color="textPrimary"
                        className="text-align-left pb-12"
                      >
                        IT Help Desk Email
                      </CustomTypography>
                      <CustomTextField
                        id="txt-facility-IT-help-desk-email"
                        placeholder="Type IT Help Desk Email"
                        size="small"
                        value={values.contactDetails.itHelpDeskEmail}
                        onChange={(event) => {
                          setFieldValue(
                            'contactDetails.itHelpDeskEmail',
                            event.target.value
                          );
                        }}
                        onBlur={handleBlur}
                      />
                      {errors?.contactDetails?.itHelpDeskEmail && (
                        <CustomTypography
                          id="validate-IT-help-desk-email"
                          color="error"
                          variant="body3"
                          className="pt-8 text-align-left"
                        >
                          {errors.contactDetails.itHelpDeskEmail}
                        </CustomTypography>
                      )}
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                      <CustomTypography
                        id="facility-ed-phone-label"
                        variant="body3"
                        color="textPrimary"
                        className="text-align-left pb-12"
                      >
                        ED Phone
                      </CustomTypography>
                      <CustomTextField
                        id="txt-facility-ed-phone"
                        placeholder="Type ED Phone"
                        size="small"
                        value={values.contactDetails.edPhone}
                        onChange={(event) => {
                          const formatted = formatPhoneNumber(
                            event.target.value
                          );
                          setFieldValue('contactDetails.edPhone', formatted);
                        }}
                        onBlur={(event) => {
                          const formatted = formatPhoneNumber(
                            event.target.value
                          );
                          setFieldValue('contactDetails.edPhone', formatted);
                        }}
                      />
                      {errors?.contactDetails?.edPhone && (
                        <CustomTypography
                          id="validate-ed-phone"
                          color="error"
                          variant="body3"
                          className="pt-8 text-align-left"
                        >
                          {errors.contactDetails.edPhone}
                        </CustomTypography>
                      )}
                    </Grid2>

                    <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                      <CustomTypography
                        id="facility-icu-phone-label"
                        variant="body3"
                        color="textPrimary"
                        className="text-align-left pb-12"
                      >
                        ICU Phone
                      </CustomTypography>
                      <CustomTextField
                        id="txt-facility-icu-phone"
                        placeholder="Type ICU Phone"
                        size="small"
                        value={values.contactDetails.icuPhone}
                        onChange={(event) => {
                          const formatted = formatPhoneNumber(
                            event.target.value
                          );
                          setFieldValue('contactDetails.icuPhone', formatted);
                        }}
                        onBlur={(event) => {
                          const formatted = formatPhoneNumber(
                            event.target.value
                          );
                          setFieldValue('contactDetails.icuPhone', formatted);
                        }}
                      />
                      {errors?.contactDetails?.icuPhone && (
                        <CustomTypography
                          id="validate-icu-phone"
                          color="error"
                          variant="body3"
                          className="pt-8 text-align-left"
                        >
                          {errors.contactDetails.icuPhone}
                        </CustomTypography>
                      )}
                    </Grid2>

                    <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                      <CustomTypography
                        id="facility-med-surg-phone-label"
                        variant="body3"
                        color="textPrimary"
                        className="text-align-left pb-12"
                      >
                        Med Surg Phone
                      </CustomTypography>
                      <CustomTextField
                        id="txt-med-surg-phone"
                        placeholder="Type Med Surg Phone"
                        size="small"
                        value={values.contactDetails.medSurgPhone}
                        onChange={(event) => {
                          const formatted = formatPhoneNumber(
                            event.target.value
                          );
                          setFieldValue(
                            'contactDetails.medSurgPhone',
                            formatted
                          );
                        }}
                        onBlur={(event) => {
                          const formatted = formatPhoneNumber(
                            event.target.value
                          );
                          setFieldValue(
                            'contactDetails.medSurgPhone',
                            formatted
                          );
                        }}
                      ></CustomTextField>
                      {errors?.contactDetails?.medSurgPhone && (
                        <CustomTypography
                          id="validate-med-surg-phone"
                          color="error"
                          variant="body3"
                          className="pt-8 text-align-left"
                        >
                          {errors.contactDetails.medSurgPhone}
                        </CustomTypography>
                      )}
                    </Grid2>

                    <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                      <CustomTypography
                        id="facility-secure-fax-label"
                        variant="body3"
                        color="textPrimary"
                        className="text-align-left pb-12"
                      >
                        Secure Fax
                      </CustomTypography>
                      <CustomTextField
                        id="txt-facility-secure-fax"
                        placeholder="Type Secure Fax"
                        size="small"
                        value={values.contactDetails.secureFax}
                        onChange={(event) => {
                          const formatted = formatPhoneNumber(
                            event.target.value
                          );
                          setFieldValue('contactDetails.secureFax', formatted);
                        }}
                        onBlur={(event) => {
                          const formatted = formatPhoneNumber(
                            event.target.value
                          );
                          setFieldValue('contactDetails.secureFax', formatted);
                        }}
                      ></CustomTextField>
                      {errors?.contactDetails?.secureFax && (
                        <CustomTypography
                          id="validate-secure-fax"
                          color="error"
                          variant="body3"
                          className="pt-8 text-align-left"
                        >
                          {errors.contactDetails.secureFax}
                        </CustomTypography>
                      )}
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                      <CustomTypography
                        id="facility-other-fax-label"
                        variant="body3"
                        color="textPrimary"
                        className="text-align-left pb-12"
                      >
                        Other Fax
                      </CustomTypography>
                      <CustomTextField
                        id="txt-other-fax"
                        placeholder="Type Other Fax"
                        size="small"
                        value={values.contactDetails.otherFax}
                        onChange={(event) => {
                          const formatted = formatPhoneNumber(
                            event.target.value
                          );
                          setFieldValue('contactDetails.otherFax', formatted);
                        }}
                        onBlur={(event) => {
                          const formatted = formatPhoneNumber(
                            event.target.value
                          );
                          setFieldValue('contactDetails.otherFax', formatted);
                        }}
                      ></CustomTextField>
                      {errors?.contactDetails?.otherFax && (
                        <CustomTypography
                          id="validate-other-fax"
                          color="error"
                          variant="body3"
                          className="pt-8 text-align-left"
                        >
                          {errors.contactDetails.otherFax}
                        </CustomTypography>
                      )}
                    </Grid2>
                    <Grid2 container spacing={4} className="width-100">
                      <Grid2 size={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}>
                        <CustomTypography
                          id="other--label"
                          variant="body3"
                          color="textPrimary"
                          className="text-align-left pb-12"
                        >
                          Other
                        </CustomTypography>
                        <CustomTextField
                          id="txt-other"
                          placeholder="Type Other"
                          size="small"
                          rows={4}
                          multiLine
                          value={values.contactDetails.other}
                          onChange={(event) => {
                            setFieldValue(
                              'contactDetails.other',
                              event.target.value
                            );
                          }}
                        ></CustomTextField>
                        {errors.contactDetails?.other && (
                          <CustomTypography
                            id="validate-other"
                            color="error"
                            variant="body3"
                            className="pt-8 text-align-left"
                          >
                            {errors.contactDetails.other}
                          </CustomTypography>
                        )}
                      </Grid2>
                      <Grid2 size={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}>
                        <CustomTypography
                          id="other-fax-description-label"
                          variant="body3"
                          color="textPrimary"
                          className="text-align-left pb-12"
                        >
                          Other Fax Description
                        </CustomTypography>
                        <CustomTextField
                          id="txt-other-fax-description"
                          placeholder="Type Other Fax Description"
                          size="small"
                          rows={4}
                          multiLine
                          value={values.contactDetails.otherFaxDescription}
                          onChange={(event) => {
                            setFieldValue(
                              'contactDetails.otherFaxDescription',
                              event.target.value
                            );
                          }}
                          onBlur={handleBlur}
                        ></CustomTextField>
                        {errors.contactDetails?.otherFaxDescription && (
                          <CustomTypography
                            id="custom-link-label"
                            variant="body3"
                            color="textPrimary"
                            className="text-align-left pb-12"
                          >
                            Custom Link <span className="color-red">*</span>
                          </CustomTypography>
                        )}
                      </Grid2>
                    </Grid2>
                  </Grid2>

                  <Box>
                    <Box className="pt-32">
                      <CustomDivider />
                    </Box>
                    <Box className="pt-32">
                      <Grid2 container spacing={4} className="width-80">
                        <Grid2
                          size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}
                        >
                          <CustomTypography
                            variant="h5"
                            className="text-align-left pb-32"
                            id="it-system-text"
                          >
                            IT System Details
                          </CustomTypography>
                        </Grid2>
                      </Grid2>
                      <Grid2 container spacing={4} className="width-80">
                        <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                          <CustomTypography
                            id="network-access-label"
                            variant="body3"
                            color="textPrimary"
                            className="text-align-left pb-12"
                          >
                            Network Access Type
                          </CustomTypography>
                          <CustomTextField
                            id="txt-network-access-type"
                            placeholder="Type Network Access"
                            size="small"
                            value={values.itSystemDetails.networkAccessType}
                            onChange={(event) => {
                              setFieldValue(
                                'itSystemDetails.networkAccessType',
                                event.target.value
                              );
                            }}
                          ></CustomTextField>
                        </Grid2>
                        <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                          <CustomTypography
                            id="emr-label"
                            variant="body3"
                            color="textPrimary"
                            className="text-align-left pb-12"
                          >
                            EMR
                          </CustomTypography>
                          <CustomTextField
                            id="txt-emr"
                            placeholder="Type EMR"
                            size="small"
                            value={values.itSystemDetails.emr}
                            onChange={(event) => {
                              setFieldValue(
                                'itSystemDetails.emr',
                                event.target.value
                              );
                            }}
                          ></CustomTextField>
                        </Grid2>
                        <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                          <CustomTypography
                            id="email-domain-secondary-label"
                            variant="body3"
                            color="textPrimary"
                            className="text-align-left pb-12"
                          >
                            EPIC Context
                          </CustomTypography>
                          <CustomTextField
                            id="txt-email-domain-secondary"
                            placeholder="Type EPIC Context"
                            size="small"
                            value={values.itSystemDetails.epicContext}
                            onChange={(event) => {
                              setFieldValue(
                                'itSystemDetails.epicContext',
                                event.target.value
                              );
                            }}
                          ></CustomTextField>
                        </Grid2>
                        <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                          <CustomTypography
                            id="network-access-label"
                            variant="body3"
                            color="textPrimary"
                            className="text-align-left pb-12"
                          >
                            Network Access URL
                          </CustomTypography>
                          <CustomTextField
                            id="txt-network-access-url"
                            placeholder="Type Network Access URL"
                            size="small"
                            value={values.itSystemDetails.networkAccessUrl}
                            onChange={(event) => {
                              setFieldValue(
                                'itSystemDetails.networkAccessUrl',
                                event.target.value
                              );
                            }}
                          ></CustomTextField>
                        </Grid2>
                        <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                          <CustomTypography
                            id="documentation-in-label"
                            variant="body3"
                            color="textPrimary"
                            className="text-align-left pb-12"
                          >
                            Documentation In
                          </CustomTypography>
                          <CustomTextField
                            id="txt-documentation-in"
                            placeholder="Type Documentation In"
                            size="small"
                            value={values.itSystemDetails.documentationIn}
                            onChange={(event) => {
                              setFieldValue(
                                'itSystemDetails.documentationIn',
                                event.target.value
                              );
                            }}
                          ></CustomTextField>
                        </Grid2>
                        <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                          <CustomTypography
                            id="training-equipment-label"
                            variant="body3"
                            color="textPrimary"
                            className="text-align-left pb-12"
                          >
                            Training Requirements
                          </CustomTypography>
                          <CustomTextField
                            id="txt-training-equipment"
                            placeholder="Type Training Requirements"
                            size="small"
                            value={values.itSystemDetails.trainingRequirements}
                            onChange={(event) => {
                              setFieldValue(
                                'itSystemDetails.trainingRequirements',
                                event.target.value
                              );
                            }}
                          ></CustomTextField>
                        </Grid2>
                        <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                          <CustomTypography
                            id="vpn-gateway-software-label"
                            variant="body3"
                            color="textPrimary"
                            className="text-align-left pb-12"
                          >
                            VPN Gateway Software
                          </CustomTypography>
                          <CustomTextField
                            id="txt-vpn-gateway-software"
                            placeholder="Type VPN Gateway Software"
                            size="small"
                            value={values.itSystemDetails.vpnGatewaySoftware}
                            onChange={(event) => {
                              setFieldValue(
                                'itSystemDetails.vpnGatewaySoftware',
                                event.target.value
                              );
                            }}
                          ></CustomTextField>
                        </Grid2>
                        <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                          <CustomTypography
                            id="telemedicine-platform-label"
                            variant="body3"
                            color="textPrimary"
                            className="text-align-left pb-12"
                          >
                            Telemedicine Platform
                          </CustomTypography>
                          <CustomTextField
                            id="txt-telemedicine-platform"
                            placeholder="Type Telemedicine Platform"
                            size="small"
                            value={values.itSystemDetails.telemedicinePlatform}
                            onChange={(event) => {
                              setFieldValue(
                                'itSystemDetails.telemedicinePlatform',
                                event.target.value
                              );
                            }}
                          ></CustomTextField>
                        </Grid2>
                        <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                          <CustomTypography
                            id="email-domain-tertiary-label"
                            variant="body3"
                            color="textPrimary"
                            className="text-align-left pb-12"
                          >
                            Portal UN/PW
                          </CustomTypography>
                          <CustomTextField
                            id="txt-portal-un-pw"
                            placeholder="Type Portal UN/PW"
                            size="small"
                            value={values.itSystemDetails.portalUnPw}
                            onChange={(event) => {
                              setFieldValue(
                                'itSystemDetails.portalUnPw',
                                event.target.value
                              );
                            }}
                          ></CustomTextField>
                        </Grid2>
                        <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                          <CustomTypography
                            id="mfa-label"
                            variant="body3"
                            color="textPrimary"
                            className="text-align-left pb-12"
                          >
                            MFA
                          </CustomTypography>
                          <CustomTextField
                            id="txt-mfa"
                            placeholder="Type MFA"
                            size="small"
                            value={values.itSystemDetails.mfa}
                            onChange={(event) => {
                              setFieldValue(
                                'itSystemDetails.mfa',
                                event.target.value
                              );
                            }}
                          ></CustomTextField>
                        </Grid2>
                        <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                          <CustomTypography
                            id="telemedicine-platform-url-label"
                            variant="body3"
                            color="textPrimary"
                            className="text-align-left pb-12"
                          >
                            Telemedicine Platform URL
                          </CustomTypography>
                          <CustomTextField
                            id="txt-telemedicine-platform-url"
                            placeholder="Type Telemedicine Platform URL"
                            size="small"
                            value={
                              values.itSystemDetails.telemedicinePlatformUrl
                            }
                            onChange={(event) => {
                              setFieldValue(
                                'itSystemDetails.telemedicinePlatformUrl',
                                event.target.value
                              );
                            }}
                          ></CustomTextField>
                        </Grid2>

                        <Grid2
                          size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}
                        ></Grid2>

                        <Grid2 size={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}>
                          <CustomTypography
                            id="devices-utilized-label"
                            variant="body3"
                            color="textPrimary"
                            className="text-align-left pb-12"
                          >
                            Devices Utilized
                          </CustomTypography>
                          <CustomTextField
                            id="txt-devices-utilized"
                            placeholder="Type Devices Utilized"
                            size="small"
                            multiLine
                            rows={4}
                            value={values.itSystemDetails.devicesUtilized}
                            onChange={(event) => {
                              setFieldValue(
                                'itSystemDetails.devicesUtilized',
                                event.target.value
                              );
                            }}
                          ></CustomTextField>
                          {errors.itSystemDetails?.devicesUtilized && (
                            <CustomTypography
                              id="validate-devices-utilized"
                              color="error"
                              variant="body3"
                              className="pt-8 text-align-left"
                            >
                              {errors.itSystemDetails?.devicesUtilized}
                            </CustomTypography>
                          )}
                        </Grid2>
                        <Grid2 size={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}>
                          <CustomTypography
                            id="it-notes-label"
                            variant="body3"
                            color="textPrimary"
                            className="text-align-left pb-12"
                          >
                            IT Notes
                          </CustomTypography>
                          <CustomTextField
                            id="txt-it-notes"
                            placeholder="Type IT Notes"
                            size="small"
                            multiLine
                            rows={4}
                            value={values.itSystemDetails.itNotes}
                            onChange={(event) => {
                              setFieldValue(
                                'itSystemDetails.itNotes',
                                event.target.value
                              );
                            }}
                          ></CustomTextField>
                          {errors.itSystemDetails?.itNotes && (
                            <CustomTypography
                              id="validate-devices-utilized"
                              color="error"
                              variant="body3"
                              className="pt-8 text-align-left"
                            >
                              {errors.itSystemDetails?.itNotes}
                            </CustomTypography>
                          )}
                        </Grid2>
                      </Grid2>
                    </Box>

                    <Box className="pt-32">
                      <CustomDivider />
                    </Box>
                    <Box className="pt-32">
                      <Grid2 container spacing={4} className="width-80">
                        <Grid2
                          size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}
                        >
                          <CustomTypography
                            variant="h5"
                            className="text-align-left pb-32"
                            id="address-details-text"
                          >
                            Address Details
                          </CustomTypography>
                        </Grid2>
                      </Grid2>
                      <Grid2 container spacing={4} className="width-80">
                        <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                          <CustomTypography
                            id="facility-physical-address-line-1-label"
                            variant="body3"
                            color="textPrimary"
                            className="text-align-left pb-12"
                          >
                            Address Line 1
                          </CustomTypography>
                          <CustomTextField
                            id="txt-facility-physical-address-line-1"
                            placeholder="Type  Address Line 1"
                            size="small"
                            value={values.address.addressLine1}
                            onChange={(event) => {
                              setFieldValue(
                                'address.addressLine1',
                                event.target.value
                              );
                            }}
                          ></CustomTextField>
                        </Grid2>
                        <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                          <CustomTypography
                            id="facility-physical-city-label"
                            variant="body3"
                            color="textPrimary"
                            className="text-align-left pb-12"
                          >
                            City
                          </CustomTypography>
                          <CustomTextField
                            id="txt-facility-physical-city"
                            placeholder="Type City"
                            size="small"
                            value={values.address.city}
                            onChange={(event) => {
                              setFieldValue('address.city', event.target.value);
                            }}
                          ></CustomTextField>
                        </Grid2>
                        <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                          <CustomTypography
                            id="facility-physical-postal-code-label"
                            variant="body3"
                            color="textPrimary"
                            className="text-align-left pb-12"
                          >
                            Postal Code
                          </CustomTypography>
                          <CustomTextField
                            id="txt-facility-physical-postal-code"
                            placeholder="Type Postal Code"
                            size="small"
                            type="number"
                            value={values.address.postalCode}
                            onChange={(event) => {
                              const formatted = validatePostalCode(
                                event.target.value
                              );
                              setFieldValue('address.postalCode', formatted);
                            }}
                          ></CustomTextField>
                          {errors.address?.postalCode &&
                            touched.address?.postalCode && (
                              <CustomTypography
                                id="facility-physical-postal-code-error"
                                variant="body3"
                                color="error"
                                className="text-align-left"
                              >
                                {errors.address.postalCode}
                              </CustomTypography>
                            )}
                        </Grid2>
                        <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                          <CustomTypography
                            id="facility-physical-address-line-2-label"
                            variant="body3"
                            color="textPrimary"
                            className="text-align-left pb-12"
                          >
                            Address Line 2
                          </CustomTypography>
                          <CustomTextField
                            id="txt-facility-physical-address-line-2"
                            placeholder="Type  Address Line 2"
                            size="small"
                            value={values.address.addressLine2}
                            onChange={(event) => {
                              setFieldValue(
                                'address.addressLine2',
                                event.target.value
                              );
                            }}
                          ></CustomTextField>
                        </Grid2>
                        <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                          <CustomTypography
                            id="facility-physical-state-label"
                            variant="body3"
                            color="textPrimary"
                            className="text-align-left pb-12"
                          >
                            State
                          </CustomTypography>
                          <CustomSelect
                            id="select-facility-physical-state"
                            placeholder="Select State"
                            size="small"
                            onChange={(value) => {
                              setFieldValue('address.state', value);
                            }}
                            selectedValue={values.address.state}
                            options={mapUsStatesToOptions(
                              facilityMetaData?.usStates
                            )}
                            fullWidth
                          ></CustomSelect>
                        </Grid2>
                        <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                          <CustomTypography
                            id="facility-physical-country-label"
                            variant="body3"
                            color="textPrimary"
                            className="text-align-left pb-12"
                          >
                            County
                          </CustomTypography>
                          <CustomTextField
                            id="txt-facility-physical-country"
                            placeholder="Type County"
                            size="small"
                            value={values.address.county}
                            onChange={(event) => {
                              setFieldValue(
                                'address.county',
                                event.target.value
                              );
                            }}
                          ></CustomTextField>
                        </Grid2>
                      </Grid2>
                    </Box>
                    <Box className="pt-32">
                      <CustomDivider />
                    </Box>
                    <Box className="pt-32">
                      <Grid2 container spacing={4} className="width-80">
                        <Grid2
                          size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}
                        >
                          <CustomTypography
                            variant="h5"
                            className="text-align-left pb-32"
                            id="facility-description-details-text"
                          >
                            Additional Details
                          </CustomTypography>
                        </Grid2>
                      </Grid2>
                      <Grid2 container spacing={4} className="width-80">
                        <Grid2 size={{ xs: 12, sm: 12, md: 8, lg: 8, xl: 8 }}>
                          <CustomTypography
                            id="facility-description-label"
                            variant="body3"
                            color="textPrimary"
                            className="text-align-left pb-12"
                          >
                            Description
                          </CustomTypography>
                          <CustomTextField
                            id="txt-facility-description"
                            placeholder="Type Description"
                            size="small"
                            multiLine
                            rows={4}
                            value={values.description}
                            onChange={(event) => {
                              setFieldValue('description', event.target.value);
                            }}
                          ></CustomTextField>
                          {errors.description && (
                            <CustomTypography
                              id="validate-fax-description"
                              color="error"
                              variant="body3"
                              className="pt-8 text-align-left"
                            >
                              {errors.description}
                            </CustomTypography>
                          )}
                        </Grid2>
                      </Grid2>
                    </Box>
                    <Box className="pt-32">
                      <Grid2 container>
                        <Grid2
                          size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}
                        >
                          <Box className="display-flex justify-end">
                            <Box className="pr-12">
                              <CustomButton
                                id="cancel-button"
                                onClick={() => {
                                  handleOpenConfirmDialog(true);
                                }}
                                variant="outlined"
                              >
                                Cancel
                              </CustomButton>
                            </Box>
                            <Box>
                              <CustomButton
                                id="save-button"
                                onClick={() => {
                                  setFacilitySubmitAction(
                                    facilityId ? 'Update' : 'Save'
                                  );
                                  handleSubmit();
                                }}
                                variant="contained"
                                // disabled={!isValid || !dirty}
                              >
                                {facilityId ? 'Update' : 'Save'}
                              </CustomButton>
                            </Box>
                          </Box>
                        </Grid2>
                      </Grid2>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Form>
          );
        }}
      </Formik>
      <Box>
        <ConfirmDiscardDialog
          open={openConfirmDialog}
          onClose={() => handleOpenConfirmDialog(false)}
          navigateTo={
            facilityId && !location.state?.isFacilityList
              ? FACILITY_VIEW_ROUTE_PATH
              : FACILITY_ROUTE_PATH
          }
          navigationState={{ facilityId }}
        />
      </Box>
    </Box>
  );
};
export default AddNewFacility;
